import React, { useState, useMemo, useEffect } from "react";
import { CalendarDays, Folder, Clock, FileText, X, Check, User, UserPlus, Search } from "lucide-react";
import { INK, INK_2, INK_3, LINE, BURGUNDY, BURGUNDY_TINT, BAD_INK, WARN_BG, WARN_INK, OK_BG, OK_INK } from "../../Bureau/bureauTokens";
import { useClients } from "../../Portefeuille/useClients";
import { useEtatGlobal } from "../../EtatGlobal.js";
import { httpClient } from "../../../HttpClient";

const CANAUX = ["Cabinet", "Visio", "Téléphone"];

function pad(n) {
  return String(n).padStart(2, "0");
}
function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const fieldLabel = "text-[11px] font-medium";
const fieldInput = "h-9 px-3 text-xs outline-none";
const fieldStyle = { border: `1px solid ${LINE}`, borderRadius: 8, color: INK };

// Fidèle à MyALFA Worksync Test V2/planning.jsx (NewRdvModal) : mêmes 3
// sections (Dossier / Créneau / Objet & note), même en-tête icon-square +
// sous-titre. Crée un vrai Evenement "Demande de rdv" (comme le formulaire
// Événements normal de l'app) — c'est la synchro côté serveur
// (createEvenement, voir plan Planning) qui pousse ensuite vers le Google
// Calendar de chaque collaborateur assigné (vous + la personne choisie
// ci-dessous) connecté.
// Le bloc "Dossier" utilise une recherche unifiée Client + Prospect (Lead),
// même principe que SignModif/clientSearch.js : une seule barre, les
// suggestions des deux sources mélangées avec un badge de type, on choisit
// en cliquant plutôt qu'en présélectionnant une catégorie. Reprend les deux
// flux jusqu'ici séparés de Lead/FormLead.js (RDV sur un prospect) et
// Evenement.js catégorie "Demande de rdv" (RDV sur un client), pour que
// Planning devienne le point d'entrée unique de création de RDV. Le
// "Collaborateur" est obligatoire dans les deux cas (nécessaire pour le mail
// de confirmation envoyé après création, voir Planning.jsx.createRdv) ; pour
// un prospect, il est pré-rempli depuis la "Personne chez Alfa" déjà
// assignée au lead si elle existe, mais reste modifiable.
// `prefill` (facultatif) vient d'un clic sur un créneau vide de la grille
// (voir WeekCalendarGrid.jsx) : pré-remplit date/heure au lieu d'aujourd'hui
// 09:00.
function NewRdvModal({ prefill, onCreate, onClose }) {
  const { data: clients } = useClients();
  const { users, utilisateur, leads } = useEtatGlobal();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null); // { type: "client"|"prospect", id, nom, prenom?, mail?, userId? }
  const [showList, setShowList] = useState(false);
  const [date, setDate] = useState(prefill?.date || todayIso());
  const [time, setTime] = useState(prefill?.time || "09:00");
  const [canal, setCanal] = useState("Cabinet");
  const [personneAlfaId, setPersonneAlfaId] = useState("");
  const [motif, setMotif] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedClientEmail, setSelectedClientEmail] = useState("");
  const [clientEmailInput, setClientEmailInput] = useState("");

  const collabOptions = (users || []).filter((u) => u.id !== utilisateur?.id);

  // Le mail de confirmation (send-rdv-email-client) a besoin de l'email du
  // client — s'il n'en a pas, on propose de le saisir ici (même logique que
  // Evenement.js, catégorie "Demande de rdv") ; sans email ni saisie, le
  // mail partira quand même, juste sans destinataire client.
  useEffect(() => {
    if (selected?.type !== "client") {
      setSelectedClientEmail("");
      setClientEmailInput("");
      return;
    }
    httpClient
      .get(`/api/clients/${selected.id}`)
      .then(({ data }) => {
        setSelectedClientEmail(data?.email || "");
        setClientEmailInput("");
      })
      .catch((err) => console.error("Erreur récupération email client :", err));
  }, [selected]);

  // Deux sources fusionnées, comme SignModif/clientSearch.js — au moins 2
  // caractères avant de chercher, même seuil que là-bas.
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    const clientMatches = (clients || [])
      .filter((c) => c.nom?.toLowerCase().includes(q))
      .map((c) => ({ type: "client", id: c.id, nom: c.nom }));
    const leadMatches = (leads || [])
      .filter((l) => l.specificite !== "archive")
      .filter((l) => `${l.prenom || ""} ${l.nom || ""}`.toLowerCase().includes(q))
      .map((l) => ({ type: "prospect", id: l.id, nom: l.nom, prenom: l.prenom, mail: l.mail, userId: l.userId }));
    return [...clientMatches, ...leadMatches].slice(0, 8);
  }, [query, clients, leads]);

  const selectItem = (item) => {
    setSelected(item);
    setQuery("");
    setShowList(false);
    // "Personne chez Alfa" déjà assignée au lead — pré-remplie mais reste
    // modifiable ensuite.
    if (item.type === "prospect" && item.userId) setPersonneAlfaId(String(item.userId));
  };

  const clearSelection = () => {
    setSelected(null);
    setQuery("");
    setPersonneAlfaId("");
  };

  const canSave = !!selected && !!date && !!time && !!motif.trim() && !!personneAlfaId && !saving;

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("categorie", "Demande de rdv");
      form.append("type", "Demande de rdv");
      if (selected.type === "client") {
        form.append("clientId", selected.id);
      } else {
        form.append("formLeadId", selected.id);
      }
      form.append("dateRdv", date);
      form.append("heureRdv", time);
      form.append("siteRdv", canal);
      form.append("personneAlfaId", personneAlfaId);
      form.append("descriptif", motif.trim());
      if (note.trim()) form.append("note", note.trim());
      const userIds = [utilisateur?.id, Number(personneAlfaId)].filter(Boolean);
      userIds.forEach((id) => form.append("userIds[]", id));

      await onCreate(
        form,
        selected.type === "client"
          ? { kind: "client", client: selected, clientEmail: selectedClientEmail, newClientEmail: clientEmailInput.trim() }
          : { kind: "prospect", lead: selected },
      );
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de la création du RDV.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center p-4" style={{ background: "rgba(31,22,32,.45)" }} onClick={onClose}>
      <div
        className="w-full flex flex-col overflow-hidden"
        style={{ maxWidth: 600, maxHeight: "88vh", background: "#fff", borderRadius: 18, boxShadow: "0 24px 60px rgba(20,14,24,.30)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3.5 px-5 py-4 flex-shrink-0" style={{ borderBottom: `1px solid ${LINE}` }}>
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{ width: 40, height: 40, borderRadius: 10, background: BURGUNDY_TINT, color: BURGUNDY }}
          >
            <CalendarDays size={19} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold m-0" style={{ color: INK }}>
              Nouveau rendez-vous
            </h2>
            <div className="text-[11px] mt-0.5" style={{ color: INK_3 }}>
              Planifier un RDV sur un dossier
            </div>
          </div>
          <button onClick={onClose} className="flex items-center justify-center flex-shrink-0" style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${LINE}` }}>
            <X size={14} style={{ color: INK_2 }} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error && (
            <p className="text-xs mb-2" style={{ color: BAD_INK }}>
              {error}
            </p>
          )}

          {/* Dossier */}
          <div className="mb-3" style={{ background: "#FFF6F8", border: "1px solid #E8C8D2", borderRadius: 10, padding: "12px 14px" }}>
            <div className="flex items-center gap-1.5 text-xs font-semibold mb-2" style={{ color: BURGUNDY }}>
              <Folder size={13} /> Dossier
            </div>

            <label className={`block ${fieldLabel} mb-1`} style={{ color: INK_2 }}>
              Client ou Prospect <span style={{ color: BAD_INK }}>*</span>
            </label>

            {selected ? (
              <div
                className="flex items-center gap-2 px-3"
                style={{ height: 36, border: `1px solid ${LINE}`, borderRadius: 8, background: "#fff" }}
              >
                {selected.type === "client" ? (
                  <User size={13} style={{ color: INK_3 }} />
                ) : (
                  <UserPlus size={13} style={{ color: INK_3 }} />
                )}
                <span className="flex-1 text-xs truncate" style={{ color: INK }}>
                  {selected.type === "client" ? selected.nom : `${selected.prenom || ""} ${selected.nom || ""}`.trim()}
                </span>
                <span
                  className="text-[10px] font-semibold px-1.5 py-0.5 flex-shrink-0"
                  style={{
                    borderRadius: 999,
                    background: selected.type === "client" ? OK_BG : WARN_BG,
                    color: selected.type === "client" ? OK_INK : WARN_INK,
                  }}
                >
                  {selected.type === "client" ? "Client" : "Prospect"}
                </span>
                <button type="button" onClick={clearSelection} className="flex-shrink-0" style={{ color: INK_3 }}>
                  <X size={13} />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search size={13} className="absolute pointer-events-none" style={{ left: 10, top: "50%", transform: "translateY(-50%)", color: INK_3 }} />
                <input
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setShowList(true);
                  }}
                  onFocus={() => setShowList(true)}
                  onBlur={() => setTimeout(() => setShowList(false), 150)}
                  placeholder="Rechercher un client ou un prospect…"
                  className={`w-full ${fieldInput}`}
                  style={{ ...fieldStyle, background: "#fff", paddingLeft: 30 }}
                />
                {showList && query.trim().length >= 2 && (
                  <div
                    className="absolute left-0 right-0 mt-1 overflow-y-auto z-10"
                    style={{ top: "100%", background: "#fff", border: `1px solid ${LINE}`, borderRadius: 8, boxShadow: "0 10px 28px rgba(0,0,0,.10)", maxHeight: 200 }}
                  >
                    {suggestions.length === 0 ? (
                      <p className="text-xs px-3 py-2.5" style={{ color: INK_3 }}>
                        Aucun résultat.
                      </p>
                    ) : (
                      suggestions.map((item) => (
                        <button
                          key={`${item.type}-${item.id}`}
                          onMouseDown={() => selectItem(item)}
                          className="w-full flex items-center gap-2 text-left text-xs px-3 py-2"
                          style={{ color: INK }}
                        >
                          {item.type === "client" ? (
                            <User size={12} style={{ color: INK_3, flexShrink: 0 }} />
                          ) : (
                            <UserPlus size={12} style={{ color: INK_3, flexShrink: 0 }} />
                          )}
                          <span className="flex-1 truncate">
                            {item.type === "client" ? item.nom : `${item.prenom || ""} ${item.nom || ""}`.trim()}
                          </span>
                          <span
                            className="text-[10px] font-semibold px-1.5 py-0.5 flex-shrink-0"
                            style={{
                              borderRadius: 999,
                              background: item.type === "client" ? OK_BG : WARN_BG,
                              color: item.type === "client" ? OK_INK : WARN_INK,
                            }}
                          >
                            {item.type === "client" ? "Client" : "Prospect"}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {selected?.type === "client" && !selectedClientEmail && (
              <div className="mt-2 space-y-1.5" style={{ background: WARN_BG, border: "1px solid #E8D5A8", borderRadius: 8, padding: "8px 10px" }}>
                <p className="text-[11px]" style={{ color: WARN_INK }}>
                  Ce client n'a pas d'adresse email enregistrée. Saisissez-en une pour lui envoyer la
                  confirmation de rendez-vous — elle sera aussi enregistrée sur sa fiche.
                </p>
                <input
                  type="email"
                  value={clientEmailInput}
                  onChange={(e) => setClientEmailInput(e.target.value)}
                  placeholder="email@exemple.com"
                  className={`w-full ${fieldInput}`}
                  style={{ ...fieldStyle, background: "#fff" }}
                />
              </div>
            )}
          </div>

          {/* Créneau */}
          <div className="mb-3" style={{ background: "#FAFAF8", border: `1px solid ${LINE}`, borderRadius: 10, padding: "12px 14px" }}>
            <div className="flex items-center gap-1.5 text-xs font-semibold mb-2" style={{ color: BURGUNDY }}>
              <Clock size={13} /> Créneau
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1">
                <span className={fieldLabel} style={{ color: INK_2 }}>
                  Date <span style={{ color: BAD_INK }}>*</span>
                </span>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={fieldInput} style={fieldStyle} />
              </label>
              <label className="flex flex-col gap-1">
                <span className={fieldLabel} style={{ color: INK_2 }}>
                  Heure <span style={{ color: BAD_INK }}>*</span>
                </span>
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={fieldInput} style={fieldStyle} />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-2.5">
              <label className="flex flex-col gap-1">
                <span className={fieldLabel} style={{ color: INK_2 }}>
                  Canal
                </span>
                <select value={canal} onChange={(e) => setCanal(e.target.value)} className={`${fieldInput} appearance-none bg-white cursor-pointer`} style={fieldStyle}>
                  {CANAUX.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className={fieldLabel} style={{ color: INK_2 }}>
                  Collaborateur <span style={{ color: BAD_INK }}>*</span>
                </span>
                <select
                  value={personneAlfaId}
                  onChange={(e) => setPersonneAlfaId(e.target.value)}
                  className={`${fieldInput} appearance-none bg-white cursor-pointer`}
                  style={fieldStyle}
                >
                  <option value="">— Choisir —</option>
                  {collabOptions.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} {u.lastName}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {/* Objet & note */}
          <div style={{ background: "#FAFAF8", border: `1px solid ${LINE}`, borderRadius: 10, padding: "12px 14px" }}>
            <div className="flex items-center gap-1.5 text-xs font-semibold mb-2" style={{ color: BURGUNDY }}>
              <FileText size={13} /> Objet &amp; note
            </div>
            <label className="flex flex-col gap-1">
              <span className={fieldLabel} style={{ color: INK_2 }}>
                Motif <span style={{ color: BAD_INK }}>*</span>
              </span>
              <input
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                placeholder="ex. Point mensuel, bilan annuel…"
                className={fieldInput}
                style={fieldStyle}
              />
            </label>
            <label className="flex flex-col gap-1 mt-2.5">
              <span className={fieldLabel} style={{ color: INK_2 }}>
                Note (facultatif)
              </span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="px-3 py-2 text-xs outline-none resize-y"
                style={fieldStyle}
              />
            </label>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3.5 flex-shrink-0" style={{ borderTop: `1px solid ${LINE}`, background: "#FAFAF8" }}>
          <button onClick={onClose} className="px-4 py-2 text-xs" style={{ border: `1px solid ${LINE}`, borderRadius: 10, color: INK_2, background: "#fff" }}>
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={!canSave}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50"
            style={{ background: BURGUNDY, borderRadius: 10 }}
          >
            <Check size={13} /> {saving ? "Création…" : "Créer le RDV"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NewRdvModal;
